import { useEffect, useState, type ReactElement } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { countdownOf, heldSecondsLeft } from '@kratispot/shared';
import { Button } from '@/components/ui/button';
import { settleFakeCheckout } from '@/lib/api';
import { leaveFor } from '@/lib/leave-for';
import { moneyOf } from '@/lib/format';
import { useTranslation } from '@/lib/i18n';

const OUTCOMES = [
  { outcome: 'paid', label: 'fakePay', tone: '' },
  { outcome: 'failed', label: 'fakeDecline', tone: 'border-red-300 text-red-700' },
  { outcome: 'cancelled', label: 'fakeCancel', tone: '' },
] as const;

// The fake provider's hosted page: the mockup's fake payment, as the redirect
// a real provider would send the visitor to. Dev and the e2e gate only. §13
export function CheckoutPage(): ReactElement {
  const { t, language } = useTranslation();
  const providerRef = useParams().providerRef as string;
  const [query] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const amount = Number(query.get('amount') ?? '0');
  const back = query.get('back') ?? '/';
  const until = query.get('until');

  async function settle(outcome: 'paid' | 'failed' | 'cancelled'): Promise<void> {
    setBusy(true);
    await settleFakeCheckout(providerRef, outcome).catch(() => undefined);
    leaveFor(back);
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-secondary p-4 px-safe pt-safe pb-safe">
      <div className="flex w-full max-w-[380px] flex-col gap-3 rounded-2xl border bg-card p-6 text-center shadow-lg">
        <b className="text-[17px] font-extrabold">{t('fakeCheckout')}</b>
        <p className="text-[12.5px] text-muted-foreground">{t('fakeCheckoutLead')}</p>
        {until !== null && <HoldCountdown until={Date.parse(until)} />}
        <p className="rounded-xl border p-3 text-[13px]">
          <span className="text-muted-foreground">{t('amountDue')}</span>{' '}
          <b data-testid="fake-amount">
            {moneyOf(amount, query.get('currency') ?? 'EUR', language)}
          </b>
          <span className="mt-1 block font-mono text-[11px] text-muted-foreground">
            {query.get('code') ?? providerRef}
          </span>
        </p>
        {OUTCOMES.map((each) => (
          <Button
            key={each.outcome}
            variant={each.outcome === 'paid' ? 'default' : 'outline'}
            className={each.tone}
            disabled={busy}
            onClick={() => void settle(each.outcome)}
          >
            {busy ? t('processing') : t(each.label)}
          </Button>
        ))}
      </div>
    </div>
  );
}

// §13: how much longer the spots wait. A real provider's page cannot say it.
function HoldCountdown({ until }: { until: number }): ReactElement {
  const { t } = useTranslation();
  const [left, setLeft] = useState(() => heldSecondsLeft(until, Date.now()));

  useEffect(() => {
    const tick = setInterval(() => setLeft(heldSecondsLeft(until, Date.now())), 1000);
    return () => clearInterval(tick);
  }, [until]);

  return (
    <p
      data-testid="hold-countdown"
      role="status"
      className={`rounded-xl border px-3 py-2 text-[12.5px] ${
        left === 0 ? 'border-red-300 bg-red-50 text-red-800' : 'text-muted-foreground'
      }`}
    >
      {left === 0 ? (
        t('holdLapsed')
      ) : (
        <>
          {t('holdCountdown')} <b className="tabular-nums">{countdownOf(left)}</b>.{' '}
          {t('holdCountdownNote')}
        </>
      )}
    </p>
  );
}
